import { useEffect } from 'react';
import { Stack, Theme } from '@mui/material';

import { useSearchAlerts } from '@services/alert';
import { useAlertStore } from '@store/AlertStore';
import { useSearchSystemMaintenanceEvents } from '@services/systemManagement';
import { DateTimeFormat, getDateFormatter } from '@utils/date';
import { format } from 'date-fns-tz';
import { AlertStatus, IAlertItem } from '@models/alert';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { Alert } from '@components/alert/Alert';
import { SystemAlertArea } from '@lio/rule-engine';
import { SystemAlertGetResponse } from '@lio/api-types';
import { useServiceWorkerUpdateStore } from '@store/ServiceWorkerUpdateStore';

export const AlertList = () => {
  const [{ isAdmin, isPortalAdmin, isAuthenticated }, impersonation, layoutPreferences] = useStore(
    (state) => [state.userAuth, state.impersonation, state.layoutPreferences],
    shallow,
  );
  const { isUsingPaymentPortal } = useStore((state) => state);

  const clockFormat = layoutPreferences.clockFormat;
  const { dismissAlert, dismissedIds, updateHeight } = useAlertStore();
  const { activeWalkthroughKey, applyUpdate, isUpdating, needRefresh } = useServiceWorkerUpdateStore(
    (state) => ({
      activeWalkthroughKey: state.activeWalkthroughKey,
      applyUpdate: state.applyUpdate,
      isUpdating: state.isUpdating,
      needRefresh: state.needRefresh,
    }),
    shallow,
  );
  const showUpdateBanner = needRefresh && !activeWalkthroughKey;

  const hasAccessToHistoricalAlerts =
    isAdmin ||
    isPortalAdmin ||
    impersonation?.userOriginalAuthState.roles.some((role) => ['PortalAdmin', 'SystemAdmin'].includes(role));

  const { alertList } = useSearchAlerts(
    {
      activeAt: hasAccessToHistoricalAlerts ? 'now' : undefined,
    },
    true,
  );

  const { data: activeMaintenanceEvent } = useSearchSystemMaintenanceEvents(
    {
      activeAt: 'now',
    },
    isAuthenticated,
  );

  const maintenanceEvent = activeMaintenanceEvent && activeMaintenanceEvent?.items[0];
  const showMaintenanceAlert = (isAdmin || isPortalAdmin) && maintenanceEvent !== undefined;
  const createdAt = maintenanceEvent?.createdAt
    ? format(new Date(maintenanceEvent?.createdAt), getDateFormatter(clockFormat, DateTimeFormat))
    : '';
  const endAt = maintenanceEvent?.endAt
    ? format(new Date(maintenanceEvent?.endAt), getDateFormatter(clockFormat, DateTimeFormat))
    : '';

  const nonDismissedAlerts = alertList?.items.filter(
    (a) => !dismissedIds.includes(a.id) && (isUsingPaymentPortal || a.area !== SystemAlertArea.PaymentPortal),
  );

  if (showMaintenanceAlert) {
    const alertAdmin: IAlertItem = {
      ...maintenanceEvent,
      id: 'isAdminMaintenance',
      type: AlertStatus.Incident,
      createdByUser: {
        id: '',
        deletedAt: new Date(),
        firstName: '',
        lastName: '',
        email: '',
      },
      updatedByUser: null,
      startAt: maintenanceEvent?.createdAt || new Date(),
      endAt: maintenanceEvent?.endAt || new Date(),
      message: `The LIO Portal is currently in Maintenance Mode from ${createdAt} to ${endAt}.`,
      link: '/portal-admin/maintenance-mode',
      linkDescription: 'Learn More',
      note: '',
      showToUnauthorizedUsers: false,
      blockPayments: false,
      area: SystemAlertArea.Portal,
    } as IAlertItem;
    nonDismissedAlerts?.push(alertAdmin);
  }
  useEffect(() => {
    // Update active alerts in the store for use by other components
    if (alertList?.items) {
      useAlertStore.setState({
        activeAlerts: alertList.items as SystemAlertGetResponse[],
        isPaymentDisabled: alertList.items?.some((alert) => alert.blockPayments === true),
      });
    }
  }, [alertList?.items]);

  useEffect(() => {
    const headerHeight = document.getElementById('header-wrapper')?.offsetHeight ?? 0;
    const apiEnvBanner = document.getElementById('api-env-banner')?.offsetHeight ?? 0;
    const editSubmissionBanner = document.getElementById('edit-submission-banner')?.offsetHeight ?? 0;
    const visibleAlertCount = (nonDismissedAlerts?.length ?? 0) + (showUpdateBanner ? 1 : 0);

    updateHeight(headerHeight + visibleAlertCount * 36 + apiEnvBanner + editSubmissionBanner);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonDismissedAlerts?.length, showUpdateBanner]);

  const UpdateRequiredAlert = (
    <Alert
      id='update-now-alert'
      type={AlertStatus.SiteUpdateAlert}
      ctaText={isUpdating ? 'Updating...' : 'Get New Version'}
      ctaDisabled={isUpdating}
      ctaFunction={() => {
        void applyUpdate();
      }}
      sx={{
        '&:not(:last-of-type)': {
          borderBottom: (theme: Theme) => theme.borders.blue,
        },
      }}
    >
      Important: Application has a new version available. Please update to ensure full functionality of the application.
    </Alert>
  );

  if (!nonDismissedAlerts || !nonDismissedAlerts.length) {
    if (showUpdateBanner) {
      return (
        <Stack id='AlertList' sx={{ width: '100%' }}>
          {UpdateRequiredAlert}
        </Stack>
      );
    }
    return null;
  }

  return (
    <Stack id='AlertList' sx={{ width: '100%' }}>
      {showUpdateBanner && UpdateRequiredAlert}
      {nonDismissedAlerts.length &&
        nonDismissedAlerts.map((alert) => (
          <Alert
            id='alert-alert-alert'
            key={alert.id}
            type={alert.type}
            dismiss={isAuthenticated ? () => dismissAlert(alert.id) : undefined}
            ctaText={alert.linkDescription ?? undefined}
            ctaUrl={alert.link ?? undefined}
            sx={{
              '&:not(:last-of-type)': {
                borderBottom: (theme: Theme) => theme.borders.blue,
              },
            }}
          >
            {alert.message}
          </Alert>
        ))}
    </Stack>
  );
};
